import { useEffect, useLayoutEffect, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { TONE } from "@/components/base/StatusMessage";
import { buttonClass } from "@/components/base/buttonStyles";
import {
  clearAll,
  dismiss,
  getServerSnapshot,
  getSnapshot,
  subscribe,
} from "@/components/base/toastStore";

/**
 * 버튼을 누른 결과를 **화면 위쪽 가운데**에 띄운다 (2026-10-02 담당자 요청).
 *
 * **왜 흐름 밖으로 옮겼나** — 2026-09-09 에 도입한 상태색 상자는 `Screen` 의
 * `heading`·`toolbar` 안에 있었고 그 둘은 `shrink-0` 이다. 바깥이 `overflow-hidden` 이라
 * 페이지가 스크롤되지 않으므로, 알림 한 줄(약 49px)이 그대로 **데이터 영역에서 빠졌다** —
 * 기본 밀도 표 1.5행, `조밀` 로는 2행이다. 게다가 자동으로 사라지지 않아
 * *"컨텐츠 영역을 차지한 채 계속 떠 있고, 알림에 따라 UI 위치가 바뀐다"* 는 지적을 받았다.
 *
 * 상자와 톤 색은 그대로 쓴다 — 2026-09-09 의 근거(*"버튼 눌렀을 때 결과가 눈에 띄지
 * 않음"*)는 아직 유효하다. **바뀐 것은 자리뿐이다.**
 *
 * **면을 칠하는 것이 이 변경의 유일한 외형 변화다.** `StatusMessage` 가 바탕을 비워 둔
 * 근거는 *표의 선택 상태(`primary-50`)·주 동작 버튼과 색 경쟁을 피한다* 였는데, 떠 있는
 * 알림은 컨텐츠 평면 밖이라 경쟁할 상대가 없다. 반대로 투명한 채로 두면 글자가 표 위에
 * 겹쳐 읽히지 않는다 — 불투명 면과 옅은 그림자는 장식이 아니라 **가독 요건**이다.
 *
 * **상단바 아래에 선다.** `pointer-events-none` 이라 클릭은 통과하지만, 상단바 위에
 * 겹치면 밀도·도움말·알림 버튼이 **보이지 않는다** — 성공 알림이야 4초지만 실패 알림은
 * 담당자가 내릴 때까지 남는다. 그렇다고 상수로 박으면 레이아웃마다 헤더가 달라
 * (담당자 51px · 기사 59px · 포털은 탭 줄까지 있어 더 높다) 어딘가는 가린다.
 * 그래서 **헤더를 재서 그 아래에 둔다** — 헤더가 바뀌어도 따라가고, 로그인 화면처럼
 * 헤더가 없으면 화면 맨 위에 선다. 상자만 클릭을 다시 받는다(`닫기` 를 눌러야 하므로).
 *
 * 레이아웃 넷(`AdminLayout`·`DriverLayout`·`CarrierLayout`·`ClientLayout`)이 각각
 * 마운트한다. 런타임에 살아 있는 것은 하나뿐이고, **라우터 안이라야 `useLocation` 을
 * 쓸 수 있다**(`App.tsx` 는 `RouterProvider` 바깥이다). 로그인 4화면은 레이아웃 밖이지만
 * 거기 알림은 `inline` 으로 남겨 뒀다 — 폼 아래라 밀 것이 없고, 문구가 바로 위
 * 입력칸에 대한 것이다.
 */
/** 헤더와 알림 사이, 그리고 헤더가 없을 때 화면 위와의 사이 */
const GAP = 12;

export default function ToastLayer() {
  const entries = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const { pathname } = useLocation();
  const [top, setTop] = useState(GAP);

  // 화면을 옮기면 전부 내린다. 실패 알림은 `닫기` 로만 사라지므로, 거두지 않으면
  // 다음 화면까지 따라가 거기서 누른 결과로 읽힌다.
  useEffect(() => clearAll(), [pathname]);

  // 헤더 아래로 내려 선다. 포털은 탭 줄이 접히며 높이가 바뀌므로 지켜본다.
  useLayoutEffect(() => {
    const header = document.querySelector("header");
    if (!header) {
      setTop(GAP);
      return;
    }
    const measure = () => setTop(Math.round(header.getBoundingClientRect().bottom) + GAP);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    return () => observer.disconnect();
  }, [pathname]);

  if (entries.length === 0) return null;

  return createPortal(
    <div
      aria-live="polite"
      style={{ top }}
      className="pointer-events-none fixed left-1/2 z-[80] flex max-w-xl -translate-x-1/2 flex-col items-center gap-2 px-4"
    >
      {entries.map(({ id, value }) => {
        // 확인할 것이 남은 톤만 담당자가 내린다. 성공·안내는 4초 뒤 스스로 사라진다.
        const closable = value.tone === "warn" || value.tone === "error";
        return (
          <div
            key={id}
            role={value.tone === "error" ? "alert" : "status"}
            className={`pointer-events-auto flex animate-toast-in items-start gap-3 rounded border bg-white px-3 py-2 text-xs leading-relaxed shadow-[0_2px_10px_rgba(0,0,0,0.08)] ${TONE[value.tone]}`}
          >
            <div className="min-w-0">
              {value.text}
              {value.warnings?.map((warning) => (
                <p key={warning} className="mt-1">
                  {warning}
                </p>
              ))}
            </div>
            {closable && (
              <button
                type="button"
                onClick={() => dismiss(id)}
                className={`${buttonClass("secondary", "compact")} mt-0.5 shrink-0`}
              >
                닫기
              </button>
            )}
          </div>
        );
      })}
    </div>,
    document.body
  );
}
